import { useEffect, useState } from "react";
import { BadgeCheck, Globe, KeyRound, Save, User, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";
import { LangDropdown } from "@/components/Header";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

const labelClass = "mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-400";

// Modale « Paramètres » du panneau admin : informations du compte, langue, mot de passe.
// Réutilise les endpoints /pro/account (accessibles à tout membre connecté, admin inclus).
export const AdminSettingsModal = ({ open, onClose, onMaintenanceChange }) => {
  const { t } = useLanguage();
  const [account, setAccount] = useState(null);
  const [name, setName] = useState("");
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(null);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAccount(null);
    setPwd({ current_password: "", new_password: "", confirm: "" });
    api
      .get("/pro/account")
      .then((r) => {
        setAccount(r.data);
        setName(r.data.name);
      })
      .catch(() => setAccount({}));
    api
      .get("/settings/maintenance")
      .then((r) => setMaintenance(!!r.data?.active))
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  // Bascule du mode maintenance : le site public est masqué, /admin et /connexion restent accessibles
  const toggleMaintenance = async () => {
    setBusy("maintenance");
    try {
      const { data } = await api.put("/admin/maintenance", { active: !maintenance });
      setMaintenance(data.active);
      onMaintenanceChange?.(data.active);
      toast.success(data.active ? t("Mode maintenance activé — le site public est masqué.") : t("Mode maintenance désactivé — le site est de nouveau accessible."));
    } catch (e) {
      toast.error(t(formatApiError(e.response?.data?.detail)));
    } finally {
      setBusy(null);
    }
  };

  const saveName = async () => {
    setBusy("profile");
    try {
      await api.patch("/pro/account", { name });
      toast.success(t("Nom mis à jour"));
    } catch (e) {
      toast.error(t(formatApiError(e.response?.data?.detail)));
    } finally {
      setBusy(null);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error(t("Les deux mots de passe ne correspondent pas"));
      return;
    }
    setBusy("pwd");
    try {
      await api.post("/pro/account/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success(t("Mot de passe modifié"));
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4" data-testid="admin-settings-modal">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-xl font-extrabold tracking-tight text-slate-900">{t("Paramètres")}</h3>
          <button
            type="button"
            data-testid="admin-settings-close"
            onClick={onClose}
            aria-label={t("Fermer")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* COMPTE */}
        <section className="mt-5" data-testid="admin-settings-account">
          <p className={labelClass}>
            <User className="h-3.5 w-3.5" />
            {t("Informations du compte")}
          </p>
          <div className="space-y-3 rounded-2xl border border-slate-100 p-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">{t("Nom complet")}</label>
              <input data-testid="admin-settings-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">{t("Courriel")}</label>
              <input data-testid="admin-settings-email" value={account?.email || ""} disabled className={`${inputClass} bg-slate-50 text-slate-400`} />
            </div>
            <div className="flex items-center justify-between rounded-2xl bg-[#F8FAFC] px-4 py-3">
              <span className="text-xs font-medium text-slate-500">{t("Rôle")}</span>
              <span data-testid="admin-settings-role" className="flex items-center gap-1.5 rounded-full bg-[#EFF6FF] px-3 py-1 text-[11px] font-bold text-[#2563EB]">
                <BadgeCheck className="h-3.5 w-3.5" />
                {t("Administrateur")}
              </span>
            </div>
            {account?.member_since && (
              <p className="text-[11px] text-slate-400">{t("Membre depuis")} {account.member_since}</p>
            )}
            <button
              type="button"
              data-testid="admin-settings-save-name"
              onClick={saveName}
              disabled={busy === "profile" || !name.trim()}
              className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-60"
            >
              <Save className="h-3.5 w-3.5" />
              {busy === "profile" ? t("Enregistrement…") : t("Enregistrer")}
            </button>
          </div>
        </section>

        {/* LANGUE */}
        <section className="mt-6" data-testid="admin-settings-lang">
          <p className={labelClass}>
            <Globe className="h-3.5 w-3.5" />
            {t("Langue")}
          </p>
          <div className="rounded-2xl border border-slate-100 p-1.5">
            <LangDropdown />
          </div>
        </section>

        {/* MODE MAINTENANCE */}
        <section className="mt-6" data-testid="admin-settings-maintenance">
          <p className={labelClass}>
            <Wrench className="h-3.5 w-3.5" />
            {t("Mode maintenance")}
          </p>
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 p-4">
            <div className="min-w-0">
              <p data-testid="maintenance-status" className={`text-sm font-bold ${maintenance ? "text-amber-600" : "text-slate-900"}`}>
                {maintenance ? t("Actif") : t("Inactif")}
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-400">
                {t("Quand il est actif, le site public affiche une page de maintenance. L'espace admin reste accessible.")}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={maintenance}
              aria-label={t("Basculer le mode maintenance")}
              data-testid="maintenance-toggle"
              onClick={toggleMaintenance}
              disabled={busy === "maintenance"}
              className={`relative h-6 w-11 shrink-0 rounded-full transition disabled:opacity-60 ${maintenance ? "bg-amber-500" : "bg-slate-300"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${maintenance ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
        </section>

        {/* MOT DE PASSE */}
        <section className="mt-6" data-testid="admin-settings-password">
          <p className={labelClass}>
            <KeyRound className="h-3.5 w-3.5" />
            {t("Mot de passe")}
          </p>
          <form onSubmit={changePassword} className="space-y-3 rounded-2xl border border-slate-100 p-4">
            <input data-testid="admin-settings-pwd-current" type="password" required value={pwd.current_password} onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} placeholder={t("Mot de passe actuel")} className={inputClass} />
            <input data-testid="admin-settings-pwd-new" type="password" required minLength={6} value={pwd.new_password} onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} placeholder={t("Nouveau mot de passe")} className={inputClass} />
            <input data-testid="admin-settings-pwd-confirm" type="password" required minLength={6} value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} placeholder={t("Confirme le nouveau mot de passe")} className={inputClass} />
            <button type="submit" data-testid="admin-settings-pwd-submit" disabled={busy === "pwd"} className="flex items-center gap-1.5 rounded-full bg-[#0F172A] px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-slate-700 disabled:opacity-60">
              <KeyRound className="h-3.5 w-3.5" />
              {busy === "pwd" ? t("Modification…") : t("Modifier le mot de passe")}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};
